import React, { useState, useRef, useEffect } from "react";
import { Menu, Bell, LogOut, ChevronDown, User, Settings } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Header({ toggleSidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const handleLogout = () => {
    setProfileOpen(false);
    logout();
    navigate("/");
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const getInitials = () => {
    if (!user || !user.email) return "U";
    const prefix = user.email.split("@")[0];
    return prefix.substring(0, 2).toUpperCase();
  };

  return (
    <header className="app-header shadow-sm">
      <div className="navbar-container">
        {/* Left Side: Toggle */}
        <div className="d-flex align-items-center">
          <button
            className="btn-toggle d-flex align-items-center justify-content-center"
            onClick={toggleSidebar}
            aria-label="Toggle Sidebar"
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Right Side: Notifications & Profile */}
        <div className="d-flex align-items-center gap-3">
          {/* Notification */}
          <button className="btn-icon position-relative" aria-label="Notifications">
            <Bell size={18} />
            <span className="badge-notification"></span>
          </button>

          {/* User Profile Dropdown */}
          <div ref={profileRef} className="position-relative">
            <div
              className="d-flex align-items-center gap-2 profile-trigger"
              onClick={() => setProfileOpen((prev) => !prev)}
              style={{ cursor: "pointer" }}
            >
              <div className="user-initials d-flex align-items-center justify-content-center fw-bold text-white shadow-sm">
                {getInitials()}
              </div>
              <div className="d-none d-md-block lh-1 text-start">
                <div className="user-name mb-1">
                  {user?.role === "admin" ? "Admin" : "Lead Manager"}
                </div>
                <div className="user-role text-muted">{user?.email || "Loading..."}</div>
              </div>
              <ChevronDown
                size={14}
                className="text-muted arrow-icon"
                style={{
                  transition: "transform 0.2s",
                  transform: profileOpen ? "rotate(180deg)" : "none",
                }}
              />
            </div>

            {profileOpen && (
              <div className="dropdown-menu-custom shadow dropdown-anim">
                <div className="dropdown-user-header">
                  <div className="d-flex align-items-center gap-2">
                    <div className="header-initials">{getInitials()}</div>
                    <div className="text-start">
                      <div className="fw-bold text-dark text-capitalize" style={{ fontSize: "13px" }}>
                        {user?.role ? `${user.role} Account` : "User Account"}
                      </div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        {user?.email}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="dropdown-item-custom"
                  onClick={() => { setProfileOpen(false); navigate("/dashboard"); }}
                >
                  <User size={14} className="me-2" /> Dashboard
                </div>
                <div
                  className="dropdown-item-custom"
                  onClick={() => { setProfileOpen(false); navigate("/leads"); }}
                >
                  <Settings size={14} className="me-2" /> Lead Directory
                </div>

                <div className="dropdown-footer">
                  <div className="dropdown-item-custom text-danger" onClick={handleLogout}>
                    <LogOut size={14} className="me-2" /> Sign Out
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}